import { useMemo, useState } from "react";
import { AnimatePresence } from "framer-motion";
import { Download } from "lucide-react";
import { ListingHeader } from "./listing/ListingHeader";
import { ListingFooter } from "./listing/ListingFooter";
import { MobileContactBar } from "./listing/MobileContactBar";
import { PhotoLightbox } from "./listing/PhotoLightbox";
import { ShowingRequestDialog } from "./listing/ShowingRequestDialog";
import { DownloadPhotosDialog } from "./listing/DownloadPhotosDialog";
import { useListingData, listingFileUrl } from "./listing/useListingData";

const protectImage = (event) => {
  if (event.target instanceof Element && event.target.closest("img, video")) event.preventDefault();
};

/** Pure photo gallery — intentionally shows ONLY the property's photos, no
 * facts/description/floorplan/etc. (those live on the Details page). */
export default function PropertyWebsiteGallery() {
  const { slug, preview, listing, error } = useListingData();
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [showingOpen, setShowingOpen] = useState(false);
  const [downloadOpen, setDownloadOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const images = useMemo(() => (listing?.files || []).filter((file) => file.is_image), [listing]);
  const ordered = useMemo(() => {
    if (!images.length) return [];
    const cover = images.find((file) => file.id === listing?.cover_file_id);
    return cover ? [cover, ...images.filter((file) => file.id !== cover.id)] : images;
  }, [images, listing]);
  const fileUrl = (file) => listingFileUrl(listing, slug, preview, file);

  if (error) return <main data-testid="public-listing-error" className="min-h-screen bg-white grid place-items-center p-8 text-center"><h1 className="text-xl font-serif-display text-[#202833]">{error}</h1></main>;
  if (!listing) return <main data-testid="public-listing-loading" className="min-h-screen grid place-items-center bg-white font-mono text-sm text-[#667085]">Loading photo gallery…</main>;

  const contact = listing.contact;
  const brandLabel = contact?.name || listing.full_address || listing.project_name;

  return (
    <main data-testid="public-property-gallery" onContextMenuCapture={protectImage} onDragStartCapture={protectImage} className="min-h-screen bg-white text-[#202833]">
      {preview && <div data-testid="public-listing-preview-banner" className="bg-[#C7A361] text-[#111820] text-center text-xs font-mono uppercase tracking-[0.14em] py-2">Admin preview — this is how the page will look to visitors</div>}
      <ListingHeader slug={slug} brandLabel={brandLabel} preview={preview} onRequestShowing={() => setShowingOpen(true)} onMenuOpenChange={setMobileMenuOpen} />

      <div className="mx-auto max-w-[1360px] px-4 sm:px-6 lg:px-10 py-10 sm:py-14">
        <div className="flex items-start justify-between flex-wrap gap-4 mb-7">
          <div>
            <h1 data-testid="public-gallery-title" className="font-serif-display text-2xl sm:text-3xl text-[#202833]">Photo Gallery</h1>
            <p className="mt-1.5 text-sm sm:text-base text-[#667085]">{listing.full_address || listing.project_name}{ordered.length > 0 ? ` · ${ordered.length} photo${ordered.length === 1 ? "" : "s"}` : ""}</p>
          </div>
          {ordered.length > 0 && (
            <button data-testid="public-gallery-download-all" onClick={() => setDownloadOpen(true)} className="inline-flex items-center gap-1.5 rounded-full border border-[#C7A361] text-[#202833] text-xs font-semibold uppercase tracking-[0.08em] px-4 py-2 hover:bg-[#F8F7F4] transition whitespace-nowrap shrink-0">
              <Download size={13} />Download All Photos
            </button>
          )}
        </div>

        {ordered.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
            {ordered.map((file, i) => (
              <button key={file.id} data-testid={`public-gallery-photo-${file.id}`} onClick={() => setLightboxIndex(i)} className="aspect-square overflow-hidden rounded-lg bg-[#F8F7F4]">
                <img src={fileUrl(file)} alt="" loading="lazy" className="h-full w-full object-cover hover:scale-105 transition-transform duration-300" />
              </button>
            ))}
          </div>
        ) : <p data-testid="public-gallery-empty" className="text-sm text-[#667085]">Photos coming soon.</p>}
      </div>

      <ListingFooter slug={slug} brandLabel={brandLabel} brokerage={contact?.company} preview={preview} />
      <MobileContactBar phone={contact?.phone} onRequestShowing={() => setShowingOpen(true)} hidden={lightboxIndex !== null || showingOpen || mobileMenuOpen} />

      <AnimatePresence>
        {lightboxIndex !== null && (
          <PhotoLightbox photos={ordered.map((f) => ({ id: f.id, url: fileUrl(f) }))} index={lightboxIndex} onIndexChange={setLightboxIndex} onClose={() => setLightboxIndex(null)} />
        )}
      </AnimatePresence>
      <ShowingRequestDialog open={showingOpen} onClose={() => setShowingOpen(false)} slug={slug} agentName={contact?.name} address={listing.full_address || listing.project_name} />
      <DownloadPhotosDialog open={downloadOpen} onClose={() => setDownloadOpen(false)} slug={slug} />
    </main>
  );
}
